import { NoneLabel } from '@/components/labels/NoneLabel';
import { HorizontalStack, VerticalStack } from '@/components/utils/linearLayouts';
import { useToast } from '@/hooks/useToast';
import { type Children, type ReturnComponent } from '@/utils/reactTypes';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import Edit from '@mui/icons-material/Edit';
import { Box, Button, IconButton, Link, TextField, Typography } from '@mui/material';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { NotesInfoIcon } from './NotesInfoIcon';

const EditCancelButtons = ({
    isEditing,
    onCancel,
    onSubmit,
    onEdit,
    disableSave,
    iconActions = false,
}: {
    isEditing: boolean;
    onCancel: () => void;
    onSubmit: () => void;
    onEdit: () => void;
    disableSave: boolean;
    iconActions?: boolean;
}): ReturnComponent => {
    if (iconActions) {
        return isEditing ? (
            <HorizontalStack gap='2px' alignItems='center' fullWidth={false}>
                <IconButton size='small' aria-label='Cancel' onClick={onCancel}>
                    <Close fontSize='small' />
                </IconButton>
                <IconButton
                    size='small'
                    color='primary'
                    aria-label='Save'
                    disabled={disableSave}
                    onClick={onSubmit}
                >
                    <Check fontSize='small' />
                </IconButton>
            </HorizontalStack>
        ) : (
            <IconButton size='small' aria-label='Edit' onClick={onEdit}>
                <Edit fontSize='small' />
            </IconButton>
        );
    }
    return isEditing ? (
        <HorizontalStack gap='2px' alignItems='center' fullWidth={false}>
            <Button
                size='small'
                variant='text'
                color='primary'
                sx={{ textTransform: 'none' }}
                onClick={onCancel}
            >
                Cancel
            </Button>
            <Button
                size='small'
                variant='contained'
                disabled={disableSave}
                sx={{
                    textTransform: 'none',
                    minWidth: '45px',
                    padding: '2px 5px',
                }}
                onClick={onSubmit}
            >
                Save
            </Button>
        </HorizontalStack>
    ) : (
        <Button
            size='small'
            variant='text'
            color='primary'
            sx={{ textTransform: 'none' }}
            onClick={onEdit}
        >
            Edit
        </Button>
    );
};

export interface NotesEditorProps {
    notes: string | null;
    canView: boolean;
    canEdit: boolean;
    onSave: (notes: string) => Promise<void>;
    notesInfoIconProps: {
        visibleToClinicians: boolean;
        headline: string;
        description: string;
    };
    captionLabel: Children;
    transformNotesBeforeSave?: (notes: string) => string | null;
    iconActions?: boolean;
    maxReadLines?: number;
    maxEditLines?: number;
    dimEditUntilHover?: boolean;
}

export const NotesEditor = ({
    notes,
    canView,
    canEdit,
    onSave,
    notesInfoIconProps,
    captionLabel,
    transformNotesBeforeSave,
    iconActions,
    maxReadLines,
    maxEditLines,
    dimEditUntilHover = false,
}: NotesEditorProps) => {
    const { showAlert } = useToast();
    const [isEditing, setIsEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isPreviewExpanded, setIsPreviewExpanded] = useState(false);
    const [isPreviewClipped, setIsPreviewClipped] = useState(false);
    const previewRef = useRef<HTMLElement>(null);

    const saved = notes ?? '';

    const isClamped = maxReadLines !== undefined && !isPreviewExpanded;

    useLayoutEffect(() => {
        const preview = previewRef.current;
        if (!preview || !isClamped || isEditing) {
            return undefined;
        }

        const measure = () => setIsPreviewClipped(preview.scrollHeight > preview.clientHeight + 1);

        const observer = new ResizeObserver(measure);
        observer.observe(preview);
        measure();

        return () => observer.disconnect();
    }, [isClamped, isEditing, saved]);

    const togglePreview = useCallback(() => setIsPreviewExpanded((wasOpen) => !wasOpen), []);
    const disableSave = draft === saved || isSaving;

    const onEdit = () => {
        setDraft(saved);
        setIsEditing(true);
    };

    const onCancel = () => {
        setIsEditing(false);
    };

    const onSubmit = async () => {
        if (draft === saved) {
            return;
        }

        setIsSaving(true);
        try {
            const notesToSave = transformNotesBeforeSave ? transformNotesBeforeSave(draft) : draft;
            await onSave(notesToSave ?? '');
            setIsEditing(false);
        } catch {
            // Stay in edit mode so the draft survives a failed save.
            showAlert('Notes could not be saved. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    if (!canView) {
        return null;
    }

    // Quiet until the column is hovered or the control itself is focused.
    const quietEditSx =
        dimEditUntilHover && !isEditing
            ? {
                  // The button paints its own colour, so the override lands on the button.
                  '& [data-notes-actions] .MuiIconButton-root': {
                      color: 'grey.400',
                      transition: 'color 120ms ease-out',
                  },
                  '&:hover [data-notes-actions] .MuiIconButton-root, & [data-notes-actions]:focus-within .MuiIconButton-root':
                      { color: 'action.active' },
              }
            : undefined;

    return (
        // No spacing because the caption header ends up being taller than in other left rail sections.
        <VerticalStack spacing={0} sx={quietEditSx}>
            <HorizontalStack>
                <NotesInfoIcon {...notesInfoIconProps}>{captionLabel}</NotesInfoIcon>
                {canEdit ? (
                    <Box data-notes-actions>
                        <EditCancelButtons
                            isEditing={isEditing}
                            onCancel={onCancel}
                            onSubmit={() => {
                                void onSubmit();
                            }}
                            onEdit={onEdit}
                            disableSave={disableSave}
                            iconActions={iconActions}
                        />
                    </Box>
                ) : undefined}
            </HorizontalStack>
            {isEditing ? (
                <TextField
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    autoFocus
                    multiline
                    fullWidth
                    variant='outlined'
                    size='small'
                    maxRows={maxEditLines}
                    InputProps={{
                        sx: {
                            fontSize: '14px',
                            lineHeight: '20px',
                            backgroundColor: 'transparent',
                        },
                    }}
                />
            ) : saved ? (
                <>
                    <Typography
                        ref={previewRef}
                        variant='body2'
                        sx={{
                            color: 'grey.600',
                            fontSize: '14px',
                            lineHeight: '20px',
                            whiteSpace: 'pre-wrap',
                            overflowWrap: 'anywhere',
                            ...(isClamped && {
                                display: '-webkit-box',
                                WebkitBoxOrient: 'vertical',
                                WebkitLineClamp: maxReadLines,
                                overflow: 'hidden',
                            }),
                        }}
                    >
                        {saved}
                    </Typography>
                    {(isPreviewClipped || isPreviewExpanded) && (
                        <Link
                            component='button'
                            variant='body2'
                            underline='always'
                            onClick={togglePreview}
                            sx={{ alignSelf: 'flex-start' }}
                        >
                            {isPreviewExpanded ? 'See less' : 'See more'}
                        </Link>
                    )}
                </>
            ) : (
                <NoneLabel sx={{ fontSize: '14px', lineHeight: '20px' }} />
            )}
        </VerticalStack>
    );
};
